/**
 * Mutable axis-aligned rectangle with position and size.
 * Transform methods (set) mutate `this` and return `this` for chaining.
 * Query methods (contains, intersects) are side-effect-free.
 * Used for both bounding boxes and area definitions (safe area, game area).
 */
export class Rect {
  constructor(
    public x: number = 0,
    public y: number = 0,
    public w: number = 0,
    public h: number = 0,
  ) {}

  get right() {
    return this.x + this.w;
  }

  get bottom() {
    return this.y + this.h;
  }

  get cx() {
    return this.x + this.w / 2;
  }

  get cy() {
    return this.y + this.h / 2;
  }

  contains(px: number, py: number): boolean {
    return px >= this.x && px <= this.right && py >= this.y && py <= this.bottom;
  }

  intersects(other: Rect): boolean {
    return this.x < other.right && this.right > other.x && this.y < other.bottom && this.bottom > other.y;
  }

  set(x: number, y: number, w: number, h: number): Rect {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    return this;
  }

  clone(): Rect {
    return new Rect(this.x, this.y, this.w, this.h);
  }

  static fromCenter(cx: number, cy: number, w: number, h: number) {
    return new Rect(cx - w / 2, cy - h / 2, w, h);
  }
}
